/**
 * portalConfigService — all portal-configuration-api.dev.camcom.ai operations.
 *
 * Every function uses portalApiClient, which automatically:
 *   - reads portalAccessToken from localStorage
 *   - attaches Authorization: Bearer <token>
 *   - handles 401 by clearing session and redirecting to /login
 *
 * No caller should ever pass a token manually to these functions.
 */

import portalApiClient from "./portalApiClient";

// ---------------------------------------------------------------------------
// No login-config helper here, deliberately.
//
// /api/login-config is an admin route guarded by an X-API-KEY, and that key
// cannot ship in a browser bundle. The removed helper POSTed without the key
// (and POST is the *add* route, not the read route), so it returned 401
// "Missing API Key in Header" on every single login; the caller swallowed the
// error and fell back to the hardcoded path, which masked it.
//
// Nothing is lost: POST /api/login already resolves the client's login URL
// server-side from the same record. To read or change a registered URL, use
// scripts/manage-login-config.mjs.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Portal configuration CRUD (all require Authorization via portalApiClient)
// ---------------------------------------------------------------------------

/**
 * GET /api/portal-config?source_name=<sourceName>
 *
 * Returns the config object on success, or null when the server has no config
 * for this source yet (404).  Only throws on unexpected errors (5xx, network).
 */
export const fetchPortalConfig = async (sourceName) => {
  console.log("[PortalConfig] Fetching portal config for:", sourceName);
  try {
    const res = await portalApiClient.get("/api/portal-config", {
      params: { source_name: sourceName },
    });
    // API wraps response: { status, message, data: { source_name, configuration } }
    // Return the inner data object so callers work with { source_name, configuration }
    const inner = res.data?.data || res.data;
    console.log("[PortalConfig] Config loaded:", inner);
    return inner;
  } catch (err) {
    const status = err.response?.status;
    if (status === 404 || status === 500) {
      // 404 = no config provisioned yet (expected on first run)
      // 500 = server-side bug when record is missing — treat same as 404
      console.info(
        `[PortalConfig] No config for "${sourceName}" (${status}) — will seed or fall back.`,
      );
      return null;
    }
    throw err;
  }
};

/**
 * POST /api/portal-config — add portal configuration for a source.
 */
export const addPortalConfig = async (sourceName, configuration) => {
  console.log("[PortalConfig] Adding portal config for:", sourceName);
  const res = await portalApiClient.post("/api/portal-config", {
    source_name: sourceName,
    configuration,
  });
  return res.data;
};

/**
 * PUT /api/portal-config/{source_name} — update portal configuration.
 */
export const updatePortalConfig = async (sourceName, configuration) => {
  console.log("[PortalConfig] Updating portal config for:", sourceName);
  const res = await portalApiClient.put(`/api/portal-config/${sourceName}`, {
    configuration,
  });
  // Normalise same as fetchPortalConfig — return inner data object
  return res.data?.data || res.data;
};

/**
 * DELETE /api/portal-config — remove portal configuration.
 */
export const deletePortalConfig = async (sourceName) => {
  console.log("[PortalConfig] Deleting portal config for:", sourceName);
  const res = await portalApiClient.delete("/api/portal-config", {
    data: { source_name: sourceName },
  });
  return res.data;
};
